import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Paper,
  styled,
  Typography,
} from '@mui/material';
import {DateTime} from 'luxon';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {getTeamSessionDetailAsync} from '../../store/teamSessionSlice';
import BackButton from '../shared/BackButton';
import FullScreenLoadingSpinner from '../shared/FullScreenLoading';
import PageSubtitle from '../shared/PageSubtitle';
import PageTitle from '../shared/PageTitle';
import SessionVideoManager from '../shared/SessionVideoManager';
import TeamShiftUtilization from '../shared/TeamShiftUtilization';
import {Roles, SessionTypes} from '../../enums';
import CreateLostTeamSessionPlayerSession from './CreateLostTeamSessionPlayerSession';
import {Team, TeamSessionSessionEvent} from '../../interfaces';
import ExportSessionModal from '../shared/ExportSessionModal';
import SubtitleContainer from '../shared/SubtitleContainer';
import PracticeEfficiency from '../shared/PracticeEfficiency/PracticeEfficiency';
import DownloadSessionJson from '../Admin/DownloadSessionJson';
import TeamSessionPlayerLoad from './TeamSessionPlayerLoad/TeamSessionPlayerLoad';
import DrillList from './DrillList';
import SessionMetaData from '../shared/SessionMetaData';
import TeamSessionPlayerList from './TeamSessionPlayerList';
import {updateTeamSessionWithXml} from '../../api/teamSessionAPI';
import DeleteTeamSession from '../Admin/DeleteTeamSession';
import AdminChangeTeamSessionType from '../Admin/AdminChangeTeamSessionType';

interface Props {}

const StyledSessionType = styled('span')(() => ({
  display: 'block',
  textTransform: 'capitalize',
}));

const TeamSessionDetail: React.FC<Props> = () => {
  const {t} = useTranslation();
  const [uploadInProgress, setUploadInProgress] = useState(false);

  const role = useAppSelector(state => state.profile?.profile?.role);
  const teamSessionStatus = useAppSelector(state => state.teamSession?.status);
  const teamSession = useAppSelector(state => state.teamSession?.teamSession);
  const teamSessionType = useAppSelector(
    state => state.teamSession?.teamSession?.type,
  );
  const teamSessionLocalDisplayStartTime = useAppSelector(
    state => state.teamSession?.teamSession?.localDisplayStartTime,
  );
  const teamSessionStartTime = useAppSelector(
    state => state.teamSession?.teamSession?.startTime,
  );
  const teamSessionEndTime = useAppSelector(
    state => state.teamSession?.teamSession?.endTime,
  );
  const teamSessionTimeZone = useAppSelector(
    state => state.teamSession?.teamSession?.timeZone,
  );
  const teamSessionVideos = useAppSelector(
    state => state.teamSession?.teamSession?.sessionVideos ?? [],
  );
  const teams = useAppSelector(state => state.team?.teams);
  const preferredUnits = useAppSelector(
    state => state.profile.profile?.preferredUnits,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dispatch = useAppDispatch();
  const {teamId, teamSessionId} = useParams();
  const teamSessionIdInt = useMemo(
    () => parseInt(teamSessionId ?? ''),
    [teamSessionId],
  );
  const teamIdInt = useMemo(() => parseInt(teamId ?? ''), [teamId]);
  const activeTeam = useMemo(
    () => teams.find((t: Team) => t.id === teamIdInt),
    [teams, teamIdInt],
  );

  const onUploadXml = useCallback(async () => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      // Read as text first to let browser handle encoding properly
      reader.readAsText(file);
      reader.onload = () => {
        try {
          const textContent = reader.result as string;

          const encoder = new TextEncoder();
          // Confirming that the file is being read as UTF-8
          const utf8Bytes = encoder.encode(textContent);

          const base64Data = window.btoa(String.fromCharCode(...utf8Bytes));

          resolve(base64Data);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = error => reject(error);
    });
  }, []);

  const onFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        try {
          const base64Data = await fileToBase64(file);
          setUploadInProgress(true);
          const res = await updateTeamSessionWithXml({
            teamSessionId: teamSessionIdInt,
            xmlFileData: base64Data,
            fileName: file.name,
          });
          if (res.data.updateTeamSession.success) {
            dispatch(getTeamSessionDetailAsync(teamSessionIdInt));
          }
          fileInputRef.current!.value = '';
          setUploadInProgress(false);
        } catch (error) {
          console.error('Upload failed:', error);
          setUploadInProgress(false);
        }
      }
    },
    [fileToBase64, teamSessionIdInt, dispatch],
  );
  useEffect(() => {
    dispatch(getTeamSessionDetailAsync(teamSessionIdInt));
  }, [dispatch, teamSessionIdInt]);

  const title = useMemo(
    () =>
      t('navigation.pageTitle', {
        pageName: t('pages.teamSessionDetail'),
      }),
    [t],
  );

  const displayStartTime = useMemo(() => {
    if (teamSessionLocalDisplayStartTime) {
      return `${DateTime.fromFormat(
        teamSessionLocalDisplayStartTime,
        'M/d/yyyy h:mm a',
      ).toFormat('EEEE M/d/yyyy h:mm a')} ${teamSessionTimeZone ?? ''}`;
    }
    if (teamSessionStartTime) {
      return DateTime.fromISO(teamSessionStartTime).toFormat(
        'EEEE M/d/yyyy h:mm a ZZZZ',
      );
    }
    return '';
  }, [
    teamSessionLocalDisplayStartTime,
    teamSessionTimeZone,
    teamSessionStartTime,
  ]);

  const teamSessionDrillEvents: TeamSessionSessionEvent[] = useMemo(() => {
    return (teamSession?.teamSessionSessionEvents?.slice() ?? [])
      .filter(event => event.drill)
      .sort(
        (a, b) =>
          new Date(a.startTime as string).getTime() -
          new Date(b.startTime as string).getTime(),
      );
  }, [teamSession?.teamSessionSessionEvents]);

  if (
    teamSessionStatus === 'loading' ||
    !teamSession ||
    (!activeTeam && role !== Roles.admin)
  ) {
    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <FullScreenLoadingSpinner />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container>
        <PageTitle>{t('pages.teamSessionDetail')}</PageTitle>
        <BackButton
          navigationUrl={
            teamSession?.teamSessionGroup
              ? `/teams/${teamId}/teamsessiongroups/${teamSession?.teamSessionGroup?.id}`
              : `/teams/${teamId}/teamsessions`
          }>
          {t('navigation.back', {
            pageName: t(
              teamSession?.teamSessionGroup
                ? 'pages.teamSessionGroupDetail'
                : 'pages.teamSessions',
            ),
          })}
        </BackButton>
        <SubtitleContainer>
          <PageSubtitle sx={{flexGrow: 1, mr: 2}}>
            <StyledSessionType>{teamSessionType}</StyledSessionType>
            {displayStartTime}
          </PageSubtitle>
          {[SessionTypes.practice, SessionTypes.skills].includes(
            teamSessionType as SessionTypes,
          ) && (
            <Button
              variant="contained"
              sx={{mr: 2, mb: 2, alignSelf: 'center'}}
              disabled={
                uploadInProgress ||
                (teamSession?.sessionVideos?.[0]?.sessionVideoSegments ?? [])
                  .length === 0
              }
              onClick={onUploadXml}>
              {t('library.uploadDrillXml')}
              <input
                type="file"
                ref={fileInputRef}
                accept=".xml"
                style={{display: 'none'}}
                onChange={onFileChange}
              />
            </Button>
          )}
          <ExportSessionModal
            buttonSx={{mb: 2, alignSelf: 'center'}}
            teamSessionId={teamSessionIdInt}
            teamSessionStartTime={teamSessionStartTime}
            teamSessionHasPuckDropEvents={
              (teamSession?.teamSessionSessionEvents ?? []).filter(
                (e: TeamSessionSessionEvent) =>
                  [
                    'PUCK_DROP_PERIOD_1',
                    'PUCK_DROP_PERIOD_2',
                    'PUCK_DROP_PERIOD_3',
                    'PUCK_DROP_OVERTIME',
                  ].indexOf(e.sessionEvent?.code as string) !== -1,
              ).length > 0
            }
            teamHasHudlConfig={teamSession?.team?.hasHudlConfig}
            teamHasSpiideoConfig={teamSession?.team?.hasSpiideoConfig}
            teamHasCatapultConfig={teamSession?.team?.hasCatapultConfig}
          />
        </SubtitleContainer>
        {[SessionTypes.practice, SessionTypes.skills].includes(
          teamSessionType as SessionTypes,
        ) &&
          (teamSession?.sessionVideos?.[0]?.sessionVideoSegments ?? [])
            .length === 0 && (
            <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
              {[SessionTypes.practice, SessionTypes.skills].includes(
                teamSessionType as SessionTypes,
              ) && (
                <Alert severity="info">
                  {t('library.uploadDrillXmlInstructions')}
                </Alert>
              )}
            </Box>
          )}
        {teamSessionType === SessionTypes.game && (
          <SessionMetaData teamSession={teamSession} />
        )}
        {role === Roles.admin && (
          <Paper sx={{p: 4, my: 4}}>
            <Typography variant="h3">{t('admin.adminOnly')}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                {activeTeam && (
                  <CreateLostTeamSessionPlayerSession
                    team={activeTeam}
                    teamSession={teamSession}
                    refresh={() =>
                      dispatch(getTeamSessionDetailAsync(teamSessionIdInt))
                    }
                  />
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                {(teamSession?.playerSessions ?? []).length > 0 && (
                  <DownloadSessionJson
                    fileTitle={`teamsession_${teamSession.id}_${DateTime.fromISO(
                      teamSessionStartTime as string,
                    ).toFormat('yyyy-MM-dd-HH-mm')}`}
                    playerSessionIds={(teamSession?.playerSessions ?? []).map(
                      ps => ps.id as number,
                    )}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                <DeleteTeamSession teamSessionId={teamSession?.id as number} />
              </Grid>
              <Grid item xs={12} md={3}>
                <AdminChangeTeamSessionType
                  teamSessionId={teamSession?.id as number}
                  sessionVideoId={teamSession?.sessionVideos?.[0]?.id as number}
                  type={teamSessionType as SessionTypes}
                  onSuccess={() =>
                    dispatch(getTeamSessionDetailAsync(teamSessionIdInt))
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        )}
        <TeamShiftUtilization teamSessions={teamSession ? [teamSession] : []} />
        {teamSessionType !== SessionTypes.game && (
          <PracticeEfficiency
            playerSessions={teamSession?.playerSessions || []}
            teamSessionStartTime={teamSessionStartTime as string}
            teamSessionEndTime={teamSessionEndTime as string}
            practiceEfficiencyMax={teamSession?.team?.practiceEfficiencyMax}
          />
        )}
        <TeamSessionPlayerLoad
          teamSessions={teamSession ? [teamSession] : []}
        />
        <PageSubtitle>{t('video.instantVideoTitle')}</PageSubtitle>
        <SessionVideoManager
          sessionVideos={teamSessionVideos ?? []}
          sessionType="team"
          teamId={teamIdInt}
          sessionId={teamSessionIdInt}
          sessionStartTime={teamSessionStartTime}
          sessionEndTime={teamSessionEndTime}
          hideVideoPlayer={teamSessionDrillEvents.length > 0}
        />
        {teamSessionDrillEvents.length > 0 && (
          <DrillList
            teamSession={teamSession}
            teamSessionDrillEvents={teamSessionDrillEvents}
            preferredUnits={preferredUnits}
          />
        )}
        <TeamSessionPlayerList
          teamId={teamIdInt}
          teamSessionId={teamSessionIdInt}
          preferredUnits={preferredUnits}
          sessionType={teamSessionType}
          data={teamSession?.playerSessions ?? []}
        />
      </Container>
    </>
  );
};

export default TeamSessionDetail;
